import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import { parse } from 'query-string';
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useMyCoachingAdherenceQuery } from '../../../queries/AIConversation/useMyCoachingAdherenceQuery';
import blitzPlayLightning1 from '../../assets/blitzPlayLightning1.png';
import blitzPlayLightning2 from '../../assets/blitzPlayLightning2.png';
import { useTysonMode } from '../../contexts/tysonMode';
import { useErrorMessages, useGamification } from '../../hooks';
import { useQueue } from '../../hooks/queue';
import { MIN_CALLS_FOR_SHARE } from '../../constants/coachingAdherence';
import { followedCoachShare } from '../../focused-view/pacing/coffeeMetrics';
import { TransparentButton } from '../TransparentButton';
import { Dialer } from '../TwilioDialer/Dialer';
import { AnimatedLionGraphic } from '../svgs/AnimatedLionGraphic';
import { BoxingGloveIcon } from '../svgs/icons/BoxingGloveIcon';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	/** Replaces the legacy dialer, for pages that run their own call pipeline outside the telephony context. */
	dialer?: React.ReactNode;
}

const StatusBannerBase: React.FC<IProps> = ({ className = '', dialer }) => {
	const gamification = useGamification();
	const errorMessages = useErrorMessages();
	const tysonMode = useTysonMode();
	const queue = useQueue();

	const userSession = useUserSession();

	const history = useHistory();

	const isLevitateSalesCoffeeAccount = userSession.account.isLevitateSalesCoffeeAccount;

	const handleToggleTysonMode = useCallback(() => {
		const search = parse(history.location.search);
		const ruleId = search.ruleId;

		if (tysonMode.isActive) {
			// Exit Tyson mode and redirect to load a regular lead
			tysonMode.exitTysonMode();
			const exitUrl = `/queue${ruleId ? `?ruleId=${ruleId}` : ''}`;

			// Always reload to get a new regular lead
			history.replace(exitUrl);
			window.location.reload();
		} else {
			// Prevent entering Tyson mode if a call has already been made on this lead
			if (queue?.hasCalled) {
				toast.error('Cannot enter Tyson mode after making a call on this lead.');
				return;
			}
			// Redirect to queue with tysonMode parameter to load a new practice lead
			const newUrl = `/queue?tysonMode=true${ruleId ? `&ruleId=${ruleId}` : ''}`;

			// If we're already on /queue, use replace and reload to trigger the new lead fetch
			if (history.location.pathname === '/queue') {
				history.replace(newUrl);
				window.location.reload();
			} else {
				history.push(newUrl);
			}
		}
	}, [tysonMode, queue?.hasCalled, history]);

	useEffect(() => {
		if (!gamification.initScoresRetrieved) {
			gamification.getScores().catch(err => {
				errorMessages.pushApiError(err);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const refreshWhenVisible = () => {
			if (document.visibilityState === 'visible') {
				gamification.getScores().catch(err => {
					errorMessages.pushApiError(err);
				});
			}
		};
		document.addEventListener('visibilitychange', refreshWhenVisible);
		return () => document.removeEventListener('visibilitychange', refreshWhenVisible);
	}, [gamification, errorMessages]);

	// Auto-enable Tyson mode based on query parameter
	useEffect(() => {
		const search = parse(history.location.search);
		if (search.tysonMode !== 'true' || tysonMode.isActive || !isLevitateSalesCoffeeAccount) {
			return;
		}
		if (history.location.pathname !== '/queue') {
			history.push(`/queue?tysonMode=true${search.ruleId ? `&ruleId=${search.ruleId}` : ''}`);
			return;
		}
		// Enter directly rather than via `handleToggleTysonMode`: that is the button handler, and its
		// enter branch re-adds this very parameter and reloads. `isActive` is plain component state, so
		// it is false again after the reload and the parameter is still there — an endless refresh.
		// `QueueContainer.componentDidMount` enters from the same parameter, and both are idempotent.
		tysonMode.enterTysonMode();

		// Remove the tysonMode query parameter after enabling so user can exit normally
		const newSearch = { ...search };
		delete newSearch.tysonMode;
		const newQueryString = Object.keys(newSearch).length > 0 ? `?${new URLSearchParams(newSearch).toString()}` : '';
		history.replace(`${history.location.pathname}${newQueryString}${history.location.hash}`);
	}, [history.location.search, tysonMode.isActive, tysonMode.enterTysonMode, isLevitateSalesCoffeeAccount, history]);

	const renderDemosCount = () => {
		const demos: JSX.Element[] = [];
		for (let i = 0; i < gamification.demos; i++) {
			demos.push(<AnimatedLionGraphic key={`demo-${i}`} animate={false} className={css(styleSheet.lion)} />);
		}

		return <div className={css(styleSheet.demosContainer)}>{demos}</div>;
	};

	const dialsColor =
		gamification.percentToGoal.dials >= 100
			? styleSheet.green
			: gamification.percentToGoal.dials >= 90
				? styleSheet.yellow
				: styleSheet.red;

	const prospectingDialsColor =
		gamification.percentToGoal.prospectingDials >= 100
			? styleSheet.green
			: gamification.percentToGoal.prospectingDials >= 90
				? styleSheet.yellow
				: styleSheet.red;

	const connectionsColor =
		gamification.percentToGoal.connections >= 100
			? styleSheet.green
			: gamification.percentToGoal.connections >= 90
				? styleSheet.yellow
				: styleSheet.red;

	const followUpsSetColor =
		gamification.percentToGoal.followUpsSet >= 100
			? styleSheet.green
			: gamification.percentToGoal.followUpsSet >= 90
				? styleSheet.yellow
				: styleSheet.red;

	const coldDemoFollowUpsSetColor =
		gamification.percentToGoal.coldDemoFollowUpsSet >= 100
			? styleSheet.green
			: gamification.percentToGoal.coldDemoFollowUpsSet >= 90
				? styleSheet.yellow
				: styleSheet.red;

	const isAllowedToSelectRule = userSession?.account?.features?.aida?.allowUsersToSelectRule;

	const handleGotoStartABlitz = () => {
		history.push('/settings/startABlitz');
	};

	const coldDemoFollowUpsSet = gamification.mDailyActivity?.score?.coldDemoFollowUpsSet || 0;
	const regularFollowUpsSet = gamification.followUpsSet - coldDemoFollowUpsSet;

	return (
		<div className={`${css(styleSheet.statusBannerContainer)} ${className}`}>
			<div className={`${css(styleSheet.statusBannerContent)}`}>
				<div style={{ width: 330 }}>{dialer ?? <Dialer />}</div>
				<div className={css(styleSheet.statsContainer)}>
					{isLevitateSalesCoffeeAccount && gamification.goals?.prospectingDials > 0 ? (
						<PacingStat metric='prospectingDials' label='Prospecting Dials' />
					) : (
						<div className={css(styleSheet.statContainer)}>
							<div className={css(styleSheet.stat, prospectingDialsColor)}>{gamification.prospectingDials}</div>
							<div className={css(styleSheet.label)}>Prospecting Dials</div>
						</div>
					)}
					{!isLevitateSalesCoffeeAccount && gamification.goals?.points > 0 ? (
						<PacingStat metric='points' label='Points' />
					) : null}
					<div className={css(styleSheet.statContainer)}>
						<div className={css(styleSheet.stat, dialsColor)}>{gamification.dials}</div>
						<div className={css(styleSheet.label)}>Dials</div>
					</div>
					<div className={css(styleSheet.statContainer)}>
						<div className={css(styleSheet.stat, connectionsColor)}>{gamification.connections}</div>
						<div className={css(styleSheet.label)}>Connections</div>
					</div>
					{gamification.goals?.followUpsSet > 0 ? (
						<div className={css(styleSheet.statContainer)}>
							<div className={css(styleSheet.stat, followUpsSetColor)}>{gamification.followUpsSet}</div>
							<div className={css(styleSheet.label)}>Follow Ups</div>
						</div>
					) : null}
					{isLevitateSalesCoffeeAccount ? (
						<div className={css(styleSheet.statContainer)}>
							<div className={css(styleSheet.stat, coldDemoFollowUpsSetColor)}>{regularFollowUpsSet}</div>
							<div className={css(styleSheet.label)}>Reg Follow Ups</div>
						</div>
					) : null}
					{isLevitateSalesCoffeeAccount ? <FollowedCoachStat /> : null}
					{gamification.demos > 0 && (
						<div className={css(styleSheet.statContainer)}>
							<div className={css(styleSheet.stat)}>{renderDemosCount()}</div>
							<div className={css(styleSheet.label)}>Demos</div>
						</div>
					)}
					{isAllowedToSelectRule && (
						<div className={css(styleSheet.startABlitzContainer)}>
							<TransparentButton className={css(styleSheet.startABlitzButton)} onClick={handleGotoStartABlitz}>
								<img
									className={css(styleSheet.blitzPlayLightning1)}
									src={blitzPlayLightning1}
									alt='Blitz Play Lightning'
								/>
								<img
									className={css(styleSheet.blitzPlayLightning2)}
									src={blitzPlayLightning2}
									alt='Blitz Play Lightning'
								/>
								Start a Blitz
							</TransparentButton>
						</div>
					)}
					{isLevitateSalesCoffeeAccount && (
						<div
							className={css(
								isAllowedToSelectRule ? styleSheet.tysonModeContainer : styleSheet.tysonModeContainerNoBlitz
							)}
						>
							<TransparentButton
								className={css(styleSheet.tysonModeButton, tysonMode.isActive && styleSheet.tysonModeButtonActive)}
								onClick={handleToggleTysonMode}
								disabled={!tysonMode.isActive && queue?.hasCalled}
							>
								<BoxingGloveIcon className={css(styleSheet.tysonModeIcon)} />
								{tysonMode.isActive ? 'Exit Tyson' : 'Tyson Mode'}
							</TransparentButton>
						</div>
					)}
				</div>
				<div style={{ width: 330 }}>&nbsp;</div>
			</div>
		</div>
	);
};

// The Levitate sales team paces on prospecting dials; every other account paces on points.
const PacingStat: React.FC<{ metric: 'prospectingDials' | 'points'; label: string }> = observer(({ metric, label }) => {
	const gamification = useGamification();
	const percentToGoal = gamification.percentToGoal[metric];
	const tone = percentToGoal >= 100 ? 'green' : percentToGoal >= 90 ? 'yellow' : 'red';
	const toneText = tone === 'green' ? styleSheet.green : tone === 'yellow' ? styleSheet.yellow : styleSheet.red;
	const toneFill =
		tone === 'green' ? styleSheet.fillGreen : tone === 'yellow' ? styleSheet.fillYellow : styleSheet.fillRed;
	const expected = Math.round(gamification.projectedScore?.[metric] || 0);
	return (
		<div
			className={css(styleSheet.statContainer, styleSheet.pacingStat)}
			title={`You should be at ${expected} by ${moment().format('h:mma')}`}
		>
			<div className={css(styleSheet.pacingTop)}>
				<div className={css(styleSheet.stat, toneText)}>{gamification[metric]}</div>
				<div className={css(styleSheet.pacingGoal)}>
					/ {gamification.goals?.[metric]} {label}
				</div>
			</div>
			<div className={css(styleSheet.pacingTrack)} aria-hidden>
				<div
					className={css(styleSheet.pacingFill, toneFill)}
					style={{ width: `${Math.min(gamification.userPercentage[metric], 100)}%` }}
				/>
				<div
					className={css(styleSheet.pacingTick)}
					style={{ left: `${Math.min(gamification.goalPercentage[metric], 100)}%` }}
				/>
			</div>
		</div>
	);
});

const FollowedCoachStat: React.FC = () => {
	const adherence = useMyCoachingAdherenceQuery();
	const { display, tone, thin } = followedCoachShare(adherence.data?.rep);
	// "n/a" has no tier; it reads red like the other stats at zero, since the dark row has no neutral stat colour.
	const toneStyle = tone === 'green' ? styleSheet.green : tone === 'yellow' ? styleSheet.yellow : styleSheet.red;
	return (
		<div
			className={css(styleSheet.statContainer)}
			title={thin ? `Fewer than ${MIN_CALLS_FOR_SHARE} calls past the hook` : undefined}
		>
			<div className={css(styleSheet.stat, toneStyle)}>{display}</div>
			<div className={css(styleSheet.label)}>Followed Coach</div>
		</div>
	);
};

export const StatusBanner = observer(StatusBannerBase);
